/**
 * Routing entry point: takes a laid-out graph and fills in every connector.
 *
 * The router itself is stateless per edge; everything graph-wide — which boxes
 * count as obstacles, how parallel edges are spread into lanes, and how much
 * search each edge may afford — is decided here.
 */
import type { FlowGraph, GraphEdge, GraphNode, Point } from '../model/types';
import { nodeRect } from '../model/types';
import type { RouteRequest, RoutingObstacle, RoutingOptions } from './router';
import { routeEdge } from './router';

/** Distance between adjacent lanes when several edges share one port. */
const LANE_SPREAD = 10;

/**
 * Above this edge count, A* is switched off entirely. The L/Z/U candidates
 * still produce a readable diagram and keep a huge graph interactive.
 */
const ASTAR_EDGE_LIMIT = 1500;

export function routeGraph(graph: FlowGraph, options?: Partial<RoutingOptions>): FlowGraph {
  if (graph.edges.length === 0) return graph;

  const nodes = new Map<string, GraphNode>(graph.nodes.map((node) => [node.id, node]));
  // Junction nodes are pinpoints that sit on the flow line; treating them as
  // obstacles would make every edge detour around its own waypoint.
  const obstacles: RoutingObstacle[] = graph.nodes
    .filter((node) => node.role !== 'junction')
    .map((node) => ({ id: node.id, rect: nodeRect(node) }));

  const lanes = assignLaneOffsets(graph.edges);
  const budget: Partial<RoutingOptions> =
    graph.edges.length > ASTAR_EDGE_LIMIT
      ? { ...options, maxExpansions: 0 }
      : { ...(options ?? {}) };

  const routed = new Map<string, { points: Point[]; labelPosition: Point | null }>();
  const ordered = [...graph.edges].sort((a, b) => compareIds(a.id, b.id));
  for (const edge of ordered) {
    const source = nodes.get(edge.source);
    const target = nodes.get(edge.target);
    // A dangling edge has no geometry to route; leave it untouched.
    if (!source || !target) continue;

    const request: RouteRequest = {
      edgeId: edge.id,
      source: { rect: nodeRect(source), port: edge.sourcePort },
      target: { rect: nodeRect(target), port: edge.targetPort },
      obstacles: obstacles.filter((o) => o.id !== edge.source && o.id !== edge.target),
      direction: graph.direction,
      laneOffset: lanes.get(edge.id) ?? 0,
    };
    routed.set(edge.id, routeEdge(request, budget));
  }

  return {
    ...graph,
    edges: graph.edges.map((edge) => {
      const result = routed.get(edge.id);
      if (!result) return edge;
      return { ...edge, points: result.points, labelPosition: result.labelPosition };
    }),
  };
}

/**
 * Spreads edges that share a port into distinct lanes, centred on the port.
 * Outgoing groups win over incoming ones: an edge that fans out from a decision
 * matters more than one arriving at a merge, and letting both apply would let
 * the two offsets cancel.
 */
function assignLaneOffsets(edges: readonly GraphEdge[]): Map<string, number> {
  const offsets = new Map<string, number>();

  for (const [, group] of groupBy(edges, (edge) => `${edge.source} ${edge.sourcePort}`)) {
    if (group.length < 2) continue;
    const sorted = [...group].sort(
      (a, b) => compareIds(a.target, b.target) || compareIds(a.id, b.id),
    );
    sorted.forEach((edge, index) => {
      offsets.set(edge.id, (index - (sorted.length - 1) / 2) * LANE_SPREAD);
    });
  }

  for (const [, group] of groupBy(edges, (edge) => `${edge.target} ${edge.targetPort}`)) {
    if (group.length < 2) continue;
    const sorted = [...group].sort(
      (a, b) => compareIds(a.source, b.source) || compareIds(a.id, b.id),
    );
    sorted.forEach((edge, index) => {
      if (offsets.has(edge.id)) return;
      offsets.set(edge.id, (index - (sorted.length - 1) / 2) * LANE_SPREAD);
    });
  }

  return offsets;
}

function groupBy(
  edges: readonly GraphEdge[],
  key: (edge: GraphEdge) => string,
): Map<string, GraphEdge[]> {
  const groups = new Map<string, GraphEdge[]>();
  for (const edge of edges) {
    const id = key(edge);
    const bucket = groups.get(id);
    if (bucket) bucket.push(edge);
    else groups.set(id, [edge]);
  }
  return groups;
}

/** Code-unit ordering — locale-independent, so routing output is reproducible. */
function compareIds(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export { DEFAULT_ROUTING_OPTIONS, routeEdge } from './router';
export type {
  RouteEndpoint,
  RouteRequest,
  RouteResult,
  RoutingObstacle,
  RoutingOptions,
} from './router';

export {
  EPSILON,
  countBends,
  countCrossings,
  isOrthogonal,
  normalizePolyline,
  orthogonalize,
  pointOnPolyline,
  polylineIntersectsRect,
  polylineLength,
  polylineToPath,
  quantize,
  segmentIntersectsRect,
  segmentsOf,
} from './polyline';
export type { PolylineSegment } from './polyline';
